import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useVoteData } from '../../context/VoteDataContext';
import { useSettings } from '../../context/SettingsContext';
import SideNav from '../SideNav/SideNav';
import BYOTable from './BYOTable';
import BYOVoteBarChart from '../VoteCharts/BYOVoteBarChart';
import BYOComparisonChart from '../VoteCharts/BYOComparisonChart';
import aflPlayerData from '../../assets/afl_player_data_2026.json';
import { Trash2 } from 'lucide-react';
import ImpliedOddsInfo from '../common/ImpliedOddsInfo';

// Team logos
import AdlLogo from '../../assets/team_logos/adl.svg';
import BlLogo from '../../assets/team_logos/bl.svg';
import CarlLogo from '../../assets/team_logos/carl.svg';
import CollLogo from '../../assets/team_logos/coll.svg';
import EssLogo from '../../assets/team_logos/ess.svg';
import FreLogo from '../../assets/team_logos/fre.svg';
import GeelLogo from '../../assets/team_logos/geel.svg';
import GcLogo from '../../assets/team_logos/gc.svg';
import GwsLogo from '../../assets/team_logos/gws.svg';
import HawLogo from '../../assets/team_logos/haw.svg';
import MelbLogo from '../../assets/team_logos/melb.svg';
import NmLogo from '../../assets/team_logos/nm.svg';
import PortLogo from '../../assets/team_logos/port.svg';
import RichLogo from '../../assets/team_logos/rich.svg';
import StkLogo from '../../assets/team_logos/stk.svg';
import SydLogo from '../../assets/team_logos/syd.svg';
import WceLogo from '../../assets/team_logos/wce.svg';
import WbdLogo from '../../assets/team_logos/wbd.svg';

// Team helper functions
const TEAM_DISPLAY_NAMES = {
  "Adelaide_Crows": "Adelaide",
  "Brisbane_Lions": "Brisbane", 
  "Carlton": "Carlton",
  "Collingwood": "Collingwood",
  "Essendon": "Essendon",
  "Fremantle": "Fremantle",
  "Geelong_Cats": "Geelong",
  "Gold_Coast_Suns": "Gold Coast",
  "GWS_Giants": "GWS",
  "Hawthorn": "Hawthorn",
  "Melbourne": "Melbourne",
  "North_Melbourne": "North Melbourne",
  "Port_Adelaide": "Port Adelaide",
  "Richmond": "Richmond",
  "St_Kilda": "St Kilda",
  "Sydney_Swans": "Sydney",
  "West_Coast_Eagles": "West Coast",
  "Western_Bulldogs": "Western Bulldogs"
};

const getTeamDisplayName = (teamName) => {
  return TEAM_DISPLAY_NAMES[teamName] || teamName;
};

const getTeamLogo = (teamName) => {
  const logos = {
    "Adelaide_Crows": AdlLogo,
    "Brisbane_Lions": BlLogo,
    "Carlton": CarlLogo,
    "Collingwood": CollLogo,
    "Essendon": EssLogo,
    "Fremantle": FreLogo,
    "Geelong_Cats": GeelLogo,
    "Gold_Coast_Suns": GcLogo,
    "GWS_Giants": GwsLogo,
    "Hawthorn": HawLogo,
    "Melbourne": MelbLogo,
    "North_Melbourne": NmLogo,
    "Port_Adelaide": PortLogo,
    "Richmond": RichLogo,
    "St_Kilda": StkLogo,
    "Sydney_Swans": SydLogo,
    "West_Coast_Eagles": WceLogo,
    "Western_Bulldogs": WbdLogo,
  };
  return logos[teamName] || null;
};

// Marks a cell that clears the threshold / wins its dataset.
const LeaderCheck = ({ className = 'w-2.5 h-2.5 mt-0.5' }) => (
  <svg className={`${className} text-emerald-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
  </svg>
);

const SearchIcon = () => (
  <svg
    className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600 pointer-events-none"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

// Small uppercase eyebrow used for field labels and section titles.
const EYEBROW_CLASS = 'block text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 mb-2';

// Search field recipe: emerald for the primary/Group A side, sky for Group B / team.
const searchInputClass = (accent = 'slate') =>
  `bg-slate-900/50 border rounded-lg pl-8 pr-8 h-10 text-xs font-mono text-white placeholder-slate-600 focus:outline-none transition-colors w-full ${
    accent === 'emerald'
      ? 'border-emerald-500/30 focus:border-emerald-500/60'
      : accent === 'sky'
      ? 'border-sky-500/30 focus:border-sky-500/60'
      : 'border-slate-700/70 focus:border-emerald-500/60'
  }`;

const DROPDOWN_PANEL_CLASS =
  'absolute z-10 w-full mt-1 bg-[#0f172a] border border-slate-700/60 rounded-xl shadow-2xl shadow-black/60 max-h-60 overflow-y-auto styled-scrollbar';

const DROPDOWN_ROW_CLASS =
  'w-full px-3 py-2 text-left hover:bg-slate-800/60 flex items-center space-x-3 transition-colors';

const CLEAR_BUTTON_CLASS =
  'absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-200 transition-colors';

const NUMBER_INPUT_CLASS =
  'bg-slate-950/60 border border-slate-700/60 rounded-md px-2 py-1.5 text-white font-mono tabular-nums w-20 text-xs focus:outline-none focus:border-emerald-500/60 transition-colors';

const BYOBrownlow = () => {
  const { user, token, isAuthenticated } = useAuth();
  const { data: voteData, isLoading: isVoteLoading, error: voteError, fetchVoteData } = useVoteData();
  const { getEnabledKeys } = useSettings();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedBetType, setSelectedBetType] = useState('players-combine-x');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [voteThreshold, setVoteThreshold] = useState(20);
  const [showPlayerDropdown, setShowPlayerDropdown] = useState(false);
  
  // New state for Players A vs B bet type
  const [selectedGroupA, setSelectedGroupA] = useState([]);
  const [selectedGroupB, setSelectedGroupB] = useState([]);
  const [searchTermA, setSearchTermA] = useState('');
  const [searchTermB, setSearchTermB] = useState('');
  const [showPlayerDropdownA, setShowPlayerDropdownA] = useState(false);
  const [showPlayerDropdownB, setShowPlayerDropdownB] = useState(false);

  // New state for Players vs Team bet type
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [searchTermTeam, setSearchTermTeam] = useState('');
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);

  // Fetch vote data once after successful login
  useEffect(() => {
    const t = token && token();
    if (isAuthenticated && t && !voteData) {
      fetchVoteData(t);
    }
  }, [isAuthenticated, token, voteData, fetchVoteData]);

  const betTypeOptions = [
    { value: 'players-combine-x', label: 'Players to combine for X votes' },
    { value: 'players-a-vs-b', label: 'Players A to combine for more than players B' },
    { value: 'players-all-poll-x', label: 'Players to all poll X votes' },
    { value: 'players-vs-team', label: 'Players to poll more than TEAM' }
  ];

  // Prepare chart data for selected players (similar to DetailedPlayerVoteModal)
  const prepareChartData = () => {
    if (!voteData?.leaderboard || selectedPlayers.length === 0) {
      return { chartCounts: [], chartLabels: [] };
    }

    const enabledKeys = getEnabledKeys();
    const allPairs = [
      { k: 'count1', label: 'BG 1' },
      { k: 'count2', label: 'BG 2' },
      { k: 'count3', label: 'BG 3' },
      { k: 'count4', label: 'BG 4' },
      { k: 'count5', label: 'BG 5' },
      { k: 'betfair', label: 'Betfair' },
      { k: 'espn', label: 'ESPN' },
      { k: 'byb', label: 'BYB' }
    ];

    const activePairs = allPairs.filter(p => enabledKeys.includes(p.k));

    // Calculate combined totals for each enabled dataset
    const combinedTotals = {};
    activePairs.forEach(pair => {
      combinedTotals[pair.k] = 0;
    });

    selectedPlayers.forEach(selectedPlayer => {
      const playerData = voteData.leaderboard.find(p => p.name === selectedPlayer.name);
      if (playerData) {
        activePairs.forEach(pair => {
          if (playerData[pair.k] !== undefined) {
            combinedTotals[pair.k] += playerData[pair.k];
          }
        });
      }
    });

    const chartCounts = activePairs.map(p => combinedTotals[p.k]);
    const chartLabels = activePairs.map(p => p.label);

    return { chartCounts, chartLabels };
  };

  const { chartCounts, chartLabels } = prepareChartData();

  // Calculate global average (BG) - same as the "votes" column in main table
  const globalAverage = chartCounts.length > 0 ? chartCounts.reduce((sum, count) => sum + count, 0) / chartCounts.length : 0;

  // Get available teams from vote data
  const availableTeams = useMemo(() => {
    if (!voteData?.leaderboard) return [];
    
    const teams = new Set();
    voteData.leaderboard.forEach(player => {
      const teamName = player.full_team_name || player.team;
      if (teamName) {
        teams.add(teamName);
      }
    });
    
    return Array.from(teams).sort();
  }, [voteData?.leaderboard]);

  // Filter available teams based on search term
  const filteredTeams = useMemo(() => {
    if (!searchTermTeam) return [];
    
    const search = searchTermTeam.toLowerCase();
    return availableTeams
      .filter(team => {
        const displayName = getTeamDisplayName(team).toLowerCase();
        return displayName.includes(search);
      })
      .slice(0, 10); // Limit to 10 results for dropdown
  }, [availableTeams, searchTermTeam]);

  // Filter available players based on search term
  const filteredPlayers = useMemo(() => {
    if (!voteData?.leaderboard || !searchTerm) return [];
    
    const search = searchTerm.toLowerCase();
    return voteData.leaderboard
      .filter(player => {
        const nameMatch = player.name.toLowerCase().includes(search);
        return nameMatch && !selectedPlayers.some(selected => selected.name === player.name);
      })
      .slice(0, 10); // Limit to 10 results for dropdown
  }, [voteData, searchTerm, selectedPlayers]);

  // Filter available players for Group A
  const filteredPlayersA = useMemo(() => {
    if (!voteData?.leaderboard || !searchTermA) return [];
    
    const search = searchTermA.toLowerCase();
    return voteData.leaderboard
      .filter(player => {
        const nameMatch = player.name.toLowerCase().includes(search);
        const notInGroupA = !selectedGroupA.some(selected => selected.name === player.name);
        const notInGroupB = !selectedGroupB.some(selected => selected.name === player.name);
        return nameMatch && notInGroupA && notInGroupB;
      })
      .slice(0, 10); // Limit to 10 results for dropdown
  }, [voteData, searchTermA, selectedGroupA, selectedGroupB]);

  // Filter available players for Group B
  const filteredPlayersB = useMemo(() => {
    if (!voteData?.leaderboard || !searchTermB) return [];
    
    const search = searchTermB.toLowerCase();
    return voteData.leaderboard
      .filter(player => {
        const nameMatch = player.name.toLowerCase().includes(search);
        const notInGroupA = !selectedGroupA.some(selected => selected.name === player.name);
        const notInGroupB = !selectedGroupB.some(selected => selected.name === player.name);
        return nameMatch && notInGroupA && notInGroupB;
      })
      .slice(0, 10); // Limit to 10 results for dropdown
  }, [voteData, searchTermB, selectedGroupA, selectedGroupB]);

  // Add player to selection
  const addPlayer = (player) => {
    setSelectedPlayers(prev => [...prev, player]);
    setSearchTerm('');
    setShowPlayerDropdown(false);
  };

  // Add player to Group A
  const addPlayerToGroupA = (player) => {
    setSelectedGroupA(prev => [...prev, player]);
    setSearchTermA('');
    setShowPlayerDropdownA(false);
  };

  // Add player to Group B
  const addPlayerToGroupB = (player) => {
    setSelectedGroupB(prev => [...prev, player]);
    setSearchTermB('');
    setShowPlayerDropdownB(false);
  };

  // Add team selection
  const addTeam = (team) => {
    setSelectedTeam(team);
    setSearchTermTeam('');
    setShowTeamDropdown(false);
  };

  // Remove player from selection
  const removePlayer = (playerName) => {
    setSelectedPlayers(prev => prev.filter(p => p.name !== playerName));
  };

  // Remove player from group (for Players A vs B bet type)
  const removePlayerFromGroup = (playerName, group) => {
    if (group === 'A') {
      setSelectedGroupA(prev => prev.filter(p => p.name !== playerName));
    } else {
      setSelectedGroupB(prev => prev.filter(p => p.name !== playerName));
    }
  };

  // Handle bet type change and reset view
  const handleBetTypeChange = (newBetType) => {
    setSelectedBetType(newBetType);
    // Reset all related state when bet type changes
    setSelectedPlayers([]);
    setSelectedGroupA([]);
    setSelectedGroupB([]);
    setSelectedTeam(null);
    setSearchTerm('');
    setSearchTermA('');
    setSearchTermB('');
    setSearchTermTeam('');
    setVoteThreshold(20);
    setShowPlayerDropdown(false);
    setShowPlayerDropdownA(false);
    setShowPlayerDropdownB(false);
    setShowTeamDropdown(false);
  };

  return (
    <div className="min-h-screen bg-[#020817] text-white">
      {/* Navigation */}
      <SideNav 
        isMobileMenuOpen={isMobileMenuOpen} 
        setIsMobileMenuOpen={setIsMobileMenuOpen} 
      />

      {/* BYO Brownlow Content */}
      <div className="md:ml-[var(--sidenav-width)] md:transition-[margin-left] md:duration-200 md:ease-out pt-3 md:pt-2 pb-20 md:pb-0 min-w-0">
        {isVoteLoading && (
          <div className="min-h-[calc(100vh-4rem)] md:min-h-[calc(100vh-0.5rem)] flex items-center justify-center px-3 md:px-4">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-slate-800 border-t-emerald-400" />
          </div>
        )}
        {voteError && (
          <div className="px-3 md:px-4 pb-3">
            <div className="bg-rose-500/10 border border-rose-500/40 text-rose-300 rounded-xl p-3 text-sm">{voteError}</div>
          </div>
        )}
        {!isVoteLoading && (
          <div className="lg:h-[calc(100vh-0.5rem)] px-3 md:px-4 pb-2 flex flex-col min-w-0 lg:overflow-hidden">
            {/* Single Combined Container */}
            <div className="flex-1 bg-[#0a1120] rounded-2xl border border-slate-800 p-3 md:p-4 flex flex-col min-h-0 min-w-0 lg:overflow-hidden">
              <div className="flex items-center gap-2 mb-2.5">
                <div className="w-1 h-4 bg-emerald-500 rounded-sm" />
                <h3 className="text-sm font-semibold text-white tracking-tight">BYOBrownlow Bet Builder</h3>
              </div>
              
              {/* BYO Bet Type Dropdown and Search Input */}
              <div className="mb-3 space-y-3">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {/* BYO Bet Type Dropdown */}
                  <div>
                    <label htmlFor="bet-type-select" className={EYEBROW_CLASS}>
                      BYO Bet Type
                    </label>
                    <select
                      id="bet-type-select"
                      value={selectedBetType}
                      onChange={(e) => handleBetTypeChange(e.target.value)}
                      className="bg-slate-900/50 border border-slate-700/70 text-white rounded-lg pl-3 pr-8 h-10 text-xs font-mono w-full focus:outline-none focus:border-emerald-500/60 cursor-pointer appearance-none transition-colors"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 8px center',
                        backgroundSize: '14px',
                      }}
                    >
                      {betTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Search Players Input - Different for A vs B and vs Team bet types */}
                  {selectedBetType === 'players-a-vs-b' ? (
                    /* Two search boxes for A vs B bet type */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Group A Search */}
                      <div>
                        <label className="block text-[10px] font-mono uppercase tracking-[0.14em] text-emerald-400 mb-2">
                          Search Players for Group A
                        </label>
                        <div className="relative">
                          <SearchIcon />
                          <input
                            type="text"
                            placeholder="Add to Group A..."
                            value={searchTermA}
                            onChange={(e) => {
                              setSearchTermA(e.target.value);
                              setShowPlayerDropdownA(true);
                            }}
                            onFocus={() => setShowPlayerDropdownA(true)}
                            className={searchInputClass('emerald')}
                          />
                          {searchTermA && (
                            <button
                              onClick={() => {
                                setSearchTermA('');
                                setShowPlayerDropdownA(false);
                              }}
                              className={CLEAR_BUTTON_CLASS}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          )}
                          {/* Group A Player Dropdown */}
                          {showPlayerDropdownA && filteredPlayersA.length > 0 && (
                            <div className={DROPDOWN_PANEL_CLASS}>
                              {filteredPlayersA.map((player) => {
                                const playerImage = aflPlayerData.find(p => p.player_name === player.name)?.image_src;
                                return (
                                  <button
                                    key={player.name}
                                    onClick={() => addPlayerToGroupA(player)}
                                    className={DROPDOWN_ROW_CLASS}
                                  >
                                    {playerImage ? (
                                      <img 
                                        src={playerImage} 
                                        alt={player.name}
                                        className="w-7 h-7 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60"
                                      />
                                    ) : (
                                      <div className="w-7 h-7 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                        <span className="text-white font-bold text-xs">{player.name.charAt(0)}</span>
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="text-white text-xs font-semibold truncate">{player.name}</div>
                                      <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500">{player.team}</div>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Group B Search */}
                      <div>
                        <label className="block text-[10px] font-mono uppercase tracking-[0.14em] text-sky-400 mb-2">
                          Search Players for Group B
                        </label>
                        <div className="relative">
                          <SearchIcon />
                          <input
                            type="text"
                            placeholder="Add to Group B..."
                            value={searchTermB}
                            onChange={(e) => {
                              setSearchTermB(e.target.value);
                              setShowPlayerDropdownB(true);
                            }}
                            onFocus={() => setShowPlayerDropdownB(true)}
                            className={searchInputClass('sky')}
                          />
                          {searchTermB && (
                            <button
                              onClick={() => {
                                setSearchTermB('');
                                setShowPlayerDropdownB(false);
                              }}
                              className={CLEAR_BUTTON_CLASS}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          )}
                          {/* Group B Player Dropdown */}
                          {showPlayerDropdownB && filteredPlayersB.length > 0 && (
                            <div className={DROPDOWN_PANEL_CLASS}>
                              {filteredPlayersB.map((player) => {
                                const playerImage = aflPlayerData.find(p => p.player_name === player.name)?.image_src;
                                return (
                                  <button
                                    key={player.name}
                                    onClick={() => addPlayerToGroupB(player)}
                                    className={DROPDOWN_ROW_CLASS}
                                  >
                                    {playerImage ? (
                                      <img 
                                        src={playerImage} 
                                        alt={player.name}
                                        className="w-7 h-7 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60"
                                      />
                                    ) : (
                                      <div className="w-7 h-7 bg-gradient-to-br from-sky-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                        <span className="text-white font-bold text-xs">{player.name.charAt(0)}</span>
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="text-white text-xs font-semibold truncate">{player.name}</div>
                                      <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500">{player.team}</div>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : selectedBetType === 'players-vs-team' ? (
                    /* Player search + Team dropdown for vs Team bet type */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Player Search */}
                      <div>
                        <label className="block text-[10px] font-mono uppercase tracking-[0.14em] text-emerald-400 mb-2">
                          Search Players for Group A
                        </label>
                        <div className="relative">
                          <SearchIcon />
                          <input
                            type="text"
                            placeholder="Add players..."
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                              setShowPlayerDropdown(true);
                            }}
                            onFocus={() => setShowPlayerDropdown(true)}
                            className={searchInputClass('emerald')}
                          />
                          {searchTerm && (
                            <button
                              onClick={() => {
                                setSearchTerm('');
                                setShowPlayerDropdown(false);
                              }}
                              className={CLEAR_BUTTON_CLASS}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          )}
                          {/* Player Dropdown */}
                          {showPlayerDropdown && filteredPlayers.length > 0 && (
                            <div className={DROPDOWN_PANEL_CLASS}>
                              {filteredPlayers.map((player) => {
                                const playerImage = aflPlayerData.find(p => p.player_name === player.name)?.image_src;
                                return (
                                  <button
                                    key={player.name}
                                    onClick={() => addPlayer(player)}
                                    className={DROPDOWN_ROW_CLASS}
                                  >
                                    {playerImage ? (
                                      <img 
                                        src={playerImage} 
                                        alt={player.name}
                                        className="w-7 h-7 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60"
                                      />
                                    ) : (
                                      <div className="w-7 h-7 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                        <span className="text-white font-bold text-xs">{player.name.charAt(0)}</span>
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="text-white text-xs font-semibold truncate">{player.name}</div>
                                      <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500">{player.team}</div>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Team Search */}
                      <div>
                        <label className="block text-[10px] font-mono uppercase tracking-[0.14em] text-sky-400 mb-2">
                          Select Team
                        </label>
                        <div className="relative">
                          <SearchIcon />
                          <input
                            type="text"
                            placeholder="Search teams..."
                            value={searchTermTeam}
                            onChange={(e) => {
                              setSearchTermTeam(e.target.value);
                              setShowTeamDropdown(true);
                            }}
                            onFocus={() => setShowTeamDropdown(true)}
                            className={searchInputClass('sky')}
                          />
                          {searchTermTeam && (
                            <button
                              onClick={() => {
                                setSearchTermTeam('');
                                setShowTeamDropdown(false);
                              }}
                              className={CLEAR_BUTTON_CLASS}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          )}
                          {/* Team Dropdown */}
                          {showTeamDropdown && filteredTeams.length > 0 && (
                            <div className={DROPDOWN_PANEL_CLASS}>
                              {filteredTeams.map((team) => {
                                const teamLogo = getTeamLogo(team);
                                return (
                                  <button
                                    key={team}
                                    onClick={() => addTeam(team)}
                                    className={DROPDOWN_ROW_CLASS}
                                  >
                                    {teamLogo ? (
                                      <img 
                                        src={teamLogo} 
                                        alt={getTeamDisplayName(team)}
                                        className="w-7 h-7 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60"
                                      />
                                    ) : (
                                      <div className="w-7 h-7 bg-gradient-to-br from-sky-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                        <span className="text-white font-bold text-xs">{getTeamDisplayName(team).charAt(0)}</span>
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="text-white text-xs font-semibold truncate">{getTeamDisplayName(team)}</div>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Single search box for other bet types */
                    <div>
                      <label className={EYEBROW_CLASS}>
                        Search Players
                      </label>
                      <div className="relative">
                        <SearchIcon />
                        <input
                          type="text"
                          placeholder="Search and add players..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setShowPlayerDropdown(true);
                          }}
                          onFocus={() => setShowPlayerDropdown(true)}
                          className={searchInputClass()}
                        />
                        {searchTerm && (
                          <button
                            onClick={() => {
                              setSearchTerm('');
                              setShowPlayerDropdown(false);
                            }}
                            className={CLEAR_BUTTON_CLASS}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        )}
                        
                        {/* Player Dropdown */}
                        {showPlayerDropdown && filteredPlayers.length > 0 && (
                          <div className={DROPDOWN_PANEL_CLASS}>
                            {filteredPlayers.map((player) => {
                              const playerImage = aflPlayerData.find(p => p.player_name === player.name)?.image_src;
                              return (
                                <button
                                  key={player.name}
                                  onClick={() => addPlayer(player)}
                                  className={DROPDOWN_ROW_CLASS}
                                >
                                  {playerImage ? (
                                    <img 
                                      src={playerImage} 
                                      alt={player.name}
                                      className="w-7 h-7 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60"
                                    />
                                  ) : (
                                    <div className="w-7 h-7 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                      <span className="text-white font-bold text-xs">{player.name.charAt(0)}</span>
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className="text-white text-xs font-semibold truncate">{player.name}</div>
                                    <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500">{player.team}</div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Main Content Area - Two Rows */}
              <div className="flex-1 flex flex-col gap-3 min-h-0 min-w-0 overflow-hidden">
                {/* Top Row - BYO Table (hidden for players-all-poll-x) */}
                {selectedBetType !== 'players-all-poll-x' && (
                  <div className="flex-shrink-0">
                    <BYOTable 
                      voteData={voteData}
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      selectedBetType={selectedBetType}
                      selectedPlayers={selectedPlayers}
                      setSelectedPlayers={setSelectedPlayers}
                      voteThreshold={voteThreshold}
                      setVoteThreshold={setVoteThreshold}
                      selectedGroupA={selectedGroupA}
                      setSelectedGroupA={setSelectedGroupA}
                      selectedGroupB={selectedGroupB}
                      setSelectedGroupB={setSelectedGroupB}
                      removePlayerFromGroup={removePlayerFromGroup}
                      selectedTeam={selectedTeam}
                      setSelectedTeam={setSelectedTeam}
                    />
                  </div>
                )}

                {/* Top Row - Summary Table for players-all-poll-x bet type */}
                {selectedBetType === 'players-all-poll-x' && selectedPlayers.length > 0 && (
                  <div className="flex-1 flex flex-col min-h-0">
                    {/* Vote Threshold Input and Player Pills for players-all-poll-x */}
                    <div className="flex flex-col space-y-2.5 mb-3">
                      <div className="flex items-center space-x-3">
                        <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 whitespace-nowrap">
                          Vote Threshold
                        </label>
                        <input
                          type="number"
                          value={voteThreshold}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value !== '' && value !== '-') {
                              const numValue = parseInt(value, 10);
                              if (!isNaN(numValue) && numValue >= 0) {
                                setVoteThreshold(numValue);
                              }
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value === '' || isNaN(parseInt(value, 10)) || parseInt(value, 10) < 0) {
                              setVoteThreshold(0);
                            } else {
                              const numValue = parseInt(value, 10);
                              setVoteThreshold(numValue);
                            }
                          }}
                          className={NUMBER_INPUT_CLASS}
                          step="1"
                          min="0"
                        />
                      </div>
                      
                      {/* Selected Players Pills - Desktop Only (reusing existing implementation) */}
                      {selectedPlayers.length > 0 && (
                        <div className="hidden md:flex flex-wrap gap-2">
                          {selectedPlayers.map((player) => {
                            const playerImageSrc = (() => {
                              const playerData = aflPlayerData.find(p => p.player_name === player.name);
                              return playerData ? playerData.image_src : null;
                            })();
                            return (
                              <div
                                key={player.name}
                                className="flex items-center space-x-2 bg-slate-900/60 rounded-full pl-1.5 pr-2.5 py-1 border border-slate-700/60"
                              >
                                {playerImageSrc && (
                                  <img 
                                    src={playerImageSrc} 
                                    alt={player.name}
                                    className="w-5 h-5 rounded-full object-cover ring-1 ring-slate-700/60"
                                  />
                                )}
                                <span className="text-slate-200 text-[11px] font-medium">{player.name}</span>
                                <button
                                  onClick={() => setSelectedPlayers(prev => prev.filter(p => p.name !== player.name))}
                                  className="text-rose-400 hover:text-rose-300 transition-colors ml-1"
                                  title={`Remove ${player.name}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Summary Table replacing BYOTable for players-all-poll-x */}
                    <div className="bg-slate-950/60 rounded-xl border border-slate-800 overflow-hidden flex flex-col flex-1 min-h-0 min-w-0">
                      <div className="flex-1 overflow-auto min-w-0 styled-scrollbar">
                        <div className="overflow-x-auto w-full">
                          {/* Players all poll X table - show individual player rows with threshold differences */}
                          <table className="w-full h-full min-w-max">
                            <thead className="sticky top-0 bg-[#0e1625] z-10">
                              <tr className="border-b border-slate-800/80">
                                <th className="text-left px-1 sm:px-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">
                                  Player
                                </th>
                                {(() => {
                                  const enabledKeys = getEnabledKeys();
                                  const displayOrder = ['count1','count2','count3','count4','count5','betfair','espn','byb'];
                                  const enabledDisplayKeys = displayOrder.filter((k) => new Set(enabledKeys).has(k));
                                  const keyToLabel = { 
                                    count1: 'BG 1', count2: 'BG 2', count3: 'BG 3', count4: 'BG 4', count5: 'BG 5', 
                                    betfair: 'Betfair', espn: 'ESPN', byb: 'BYB' 
                                  };
                                  return enabledDisplayKeys.map((key, index) => (
                                    <th key={index} className="text-center px-1 sm:px-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">
                                      {keyToLabel[key] === 'Betfair' ? (
                                        <>
                                          <span className="hidden sm:inline">Betfair</span>
                                          <span className="inline sm:hidden">BF</span>
                                        </>
                                      ) : keyToLabel[key]}
                                    </th>
                                  ));
                                })()}
                                <th className="text-center px-1 sm:px-2 py-2.5 text-[10px] font-mono font-medium text-emerald-300 uppercase tracking-[0.12em] bg-emerald-500/10 whitespace-nowrap">
                                  BG
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const enabledKeys = getEnabledKeys();
                                const displayOrder = ['count1','count2','count3','count4','count5','betfair','espn','byb'];
                                const enabledDisplayKeys = displayOrder.filter((k) => new Set(enabledKeys).has(k));

                                // Create rows for each player
                                const playerRows = selectedPlayers.map((selectedPlayer, playerIndex) => {
                                  const playerData = voteData.leaderboard.find(p => p.name === selectedPlayer.name);
                                  if (!playerData) return null;

                                  // Calculate player's average from enabled datasets
                                  const validKeys = enabledKeys.filter(k => playerData[k] !== undefined);
                                  const playerAverage = validKeys.length > 0 
                                    ? validKeys.reduce((acc, k) => acc + (Number(playerData[k]) || 0), 0) / validKeys.length 
                                    : 0;
                                  
                                  const bgDifference = playerAverage - voteThreshold;
                                  const bgIsWinner = playerAverage >= voteThreshold;

                                  return (
                                    <tr key={selectedPlayer.name} className="border-b border-slate-800/50 hover:bg-slate-800/40 transition-colors">
                                      {/* Player column with photo and team info */}
                                      <td className="px-1 sm:px-2 py-2 text-left">
                                        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                                          <div className="flex-shrink-0">
                                            {(() => {
                                              const playerImageSrc = aflPlayerData.find(p => p.player_name === selectedPlayer.name)?.image_src;
                                              return playerImageSrc ? (
                                                <img 
                                                  src={playerImageSrc} 
                                                  alt={selectedPlayer.name}
                                                  className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover ring-1 ring-slate-700/60"
                                                />
                                              ) : (
                                                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                                  <span className="text-white font-bold text-xs">{selectedPlayer.name.charAt(0)}</span>
                                                </div>
                                              );
                                            })()}
                                          </div>
                                          <div className="min-w-0 flex-1">
                                            <div className="text-white font-semibold text-xs sm:text-[13px] truncate">
                                              {selectedPlayer.name}
                                            </div>
                                            <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500 truncate">
                                              {playerData?.team || 'Unknown'}
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                      
                                      {/* Count columns with differences */}
                                      {enabledDisplayKeys.map((key, index) => {
                                        const playerValue = Number(playerData[key] ?? 0);
                                        const difference = playerValue - voteThreshold;
                                        const isWinner = playerValue >= voteThreshold;
                                        
                                        return (
                                          <td key={index} className={`px-1 sm:px-2 py-2 text-center whitespace-nowrap ${isWinner ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : ''}`}>
                                            <div className="flex flex-col items-center">
                                              <span className="text-slate-300 font-mono font-semibold tabular-nums text-xs sm:text-[13px]">
                                                {playerValue.toFixed(2)}
                                              </span>
                                              <span className={`text-[10px] font-mono tabular-nums mt-0.5 ${difference >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                                {difference >= 0 ? '+' : ''}{difference.toFixed(2)}
                                              </span>
                                              {isWinner && (
                                                <LeaderCheck />
                                              )}
                                            </div>
                                          </td>
                                        );
                                      })}
                                      
                                      {/* BG column */}
                                      <td className={`px-1 sm:px-2 py-2 text-center whitespace-nowrap ${bgIsWinner ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : ''}`}>
                                        <div className="flex flex-col items-center">
                                          <span className="text-slate-300 font-mono font-semibold tabular-nums text-xs sm:text-[13px]">
                                            {playerAverage.toFixed(2)}
                                          </span>
                                          <span className={`text-[10px] font-mono tabular-nums mt-0.5 ${bgDifference >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                            {bgDifference >= 0 ? '+' : ''}{bgDifference.toFixed(2)}
                                          </span>
                                          {bgIsWinner && (
                                            <LeaderCheck />
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                });

                                // Add summary row showing implied odds
                                let countsWithAllPlayers = 0;
                                enabledDisplayKeys.forEach(key => {
                                  let playersAboveThreshold = 0;
                                  selectedPlayers.forEach(selectedPlayer => {
                                    const playerData = voteData.leaderboard.find(p => p.name === selectedPlayer.name);
                                    if (playerData && Number(playerData[key] ?? 0) >= voteThreshold) {
                                      playersAboveThreshold++;
                                    }
                                  });
                                  if (playersAboveThreshold === selectedPlayers.length && selectedPlayers.length > 0) {
                                    countsWithAllPlayers++;
                                  }
                                });
                                
                                const hitPct = enabledDisplayKeys.length > 0 ? (countsWithAllPlayers / enabledDisplayKeys.length) * 100 : 0;

                                const summaryRow = (
                                  <tr key="summary" className="border-b border-slate-800/50 bg-slate-800/20">
                                    <td className="px-1 sm:px-2 py-2 text-left whitespace-nowrap">
                                      <span className="inline-flex items-center text-[10px] font-mono uppercase tracking-[0.12em] text-emerald-300">
                                        Implied Odds<ImpliedOddsInfo />
                                      </span>
                                    </td>
                                    {enabledDisplayKeys.map((key, index) => {
                                      // Count how many selected players meet the threshold for this count
                                      let playersAboveThreshold = 0;
                                      selectedPlayers.forEach(selectedPlayer => {
                                        const playerData = voteData.leaderboard.find(p => p.name === selectedPlayer.name);
                                        if (playerData && Number(playerData[key] ?? 0) >= voteThreshold) {
                                          playersAboveThreshold++;
                                        }
                                      });
                                      
                                      const allPlayersAboveThreshold = playersAboveThreshold === selectedPlayers.length && selectedPlayers.length > 0;
                                      
                                      return (
                                        <td key={index} className={`px-1 sm:px-2 py-2 text-center whitespace-nowrap ${allPlayersAboveThreshold ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : ''}`}>
                                          <div className="flex flex-col items-center">
                                            <span className="text-slate-300 font-mono font-semibold tabular-nums text-xs sm:text-[13px]">
                                              {playersAboveThreshold}/{selectedPlayers.length}
                                            </span>
                                            {allPlayersAboveThreshold && (
                                              <LeaderCheck />
                                            )}
                                          </div>
                                        </td>
                                      );
                                    })}
                                    <td className="px-1 sm:px-2 py-2 text-center whitespace-nowrap bg-emerald-500/[0.06]">
                                      <div className="flex flex-col items-center gap-0.5">
                                        {hitPct === 0 ? (
                                          <span className="text-slate-600 text-[11px] font-mono">N/A</span>
                                        ) : (
                                          <>
                                            <span className="text-emerald-300 font-mono font-bold tabular-nums text-xs sm:text-[13px]">
                                              {(1 / (hitPct / 100)).toFixed(2)}
                                            </span>
                                            <span className="text-slate-500 text-[10px] font-mono tabular-nums">
                                              {countsWithAllPlayers}/{enabledDisplayKeys.length}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );

                                return [...playerRows, summaryRow];
                              })()}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Empty state for players-all-poll-x when no players selected */}
                {selectedBetType === 'players-all-poll-x' && selectedPlayers.length === 0 && (
                  <div className="flex-1 flex flex-col min-h-0">
                    <div className="bg-slate-950/60 rounded-xl border border-slate-800 flex-1 flex items-center justify-center">
                      <p className="text-slate-500 text-xs font-mono">Add players to begin analysis</p>
                    </div>
                  </div>
                )}

                {/* Bottom Row - Vote Visualization (hidden for players-all-poll-x) */}
                {selectedBetType !== 'players-all-poll-x' && (
                  <div className="flex-1 flex flex-col lg:flex-row gap-3 min-h-0 min-w-0 lg:overflow-hidden">
                  {/* Left Side - Selected Players Display (exclude players-all-poll-x) */}
                  {((selectedBetType === 'players-combine-x' && selectedPlayers.length > 0 && chartCounts.length > 0) ||
                    (selectedBetType === 'players-a-vs-b' && (selectedGroupA.length > 0 || selectedGroupB.length > 0)) ||
                    (selectedBetType === 'players-vs-team' && (selectedPlayers.length > 0 || selectedTeam))) && (
                    <div className="w-full lg:flex-1 flex flex-col min-w-0 min-h-0">
                      <div className="bg-slate-950/60 rounded-xl border border-slate-800 p-3 flex-shrink-0 lg:flex-1 max-h-[300px] lg:max-h-none overflow-hidden min-h-0">
                        <h4 className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 mb-2">
                          {selectedBetType === 'players-a-vs-b' 
                            ? `Selected Players (A: ${selectedGroupA.length}, B: ${selectedGroupB.length})`
                            : selectedBetType === 'players-vs-team'
                            ? `Players (${selectedPlayers.length}) vs Team${selectedTeam ? ` (${getTeamDisplayName(selectedTeam)})` : ''}`
                            : selectedBetType === 'players-all-poll-x'
                            ? `All Poll X Players (${selectedPlayers.length})`
                            : `Selected Players (${selectedPlayers.length})`}
                        </h4>
                        <div className="overflow-y-auto max-h-[250px] lg:max-h-none styled-scrollbar">
                          {selectedBetType === 'players-a-vs-b' ? (
                            // Display both groups for A vs B bet type
                            <>
                              {selectedGroupA.length > 0 && (
                                <div className="mb-3">
                                  <h5 className="text-[10px] font-mono uppercase tracking-[0.14em] text-emerald-400 mb-2">Group A</h5>
                                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-1.5">
                                    {selectedGroupA.map((player) => {
                                      const enabledKeys = getEnabledKeys();
                                      const playerData = voteData.leaderboard.find(p => p.name === player.name);
                                      const playerImageSrc = playerData ? aflPlayerData.find(p => p.player_name === playerData.name)?.image_src : null;
                                      
                                      // Calculate player's average from enabled datasets
                                      const playerAverage = playerData ? (() => {
                                        const validKeys = enabledKeys.filter(k => playerData[k] !== undefined);
                                        if (validKeys.length === 0) return 0;
                                        const sum = validKeys.reduce((acc, k) => acc + (Number(playerData[k]) || 0), 0);
                                        return sum / validKeys.length;
                                      })() : 0;

                                      return (
                                        <div key={player.name} className="bg-slate-900/60 rounded-lg border border-slate-700/60 p-2 text-center relative group overflow-hidden hover:border-slate-600 transition-colors">
                                          {/* Remove button */}
                                          <button
                                            onClick={() => removePlayerFromGroup(player.name, 'A')}
                                            className="absolute top-1 right-1 w-4 h-4 bg-rose-500/90 hover:bg-rose-500 rounded-full flex items-center justify-center text-white opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
                                            title={`Remove ${player.name}`}
                                          >
                                            <Trash2 className="w-2.5 h-2.5" />
                                          </button>
                                          <div className="flex flex-col items-center space-y-1">
                                            {playerImageSrc ? (
                                              <img 
                                                src={playerImageSrc} 
                                                alt={player.name}
                                                className="w-8 h-8 rounded-full object-cover ring-1 ring-slate-700/60"
                                              />
                                            ) : (
                                              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center ring-1 ring-slate-700/60">
                                                <span className="text-white font-bold text-xs">{player.name.charAt(0)}</span>
                                              </div>
                                            )}
                                            <div className="w-full">
                                              <div className="text-white text-xs font-semibold truncate">
                                                {player.name}
                                              </div>
                                              <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500 truncate">
                                                {playerData?.team || 'Unknown'}
                                              </div>
                                              <div className="text-emerald-300 text-xs font-mono font-bold tabular-nums">
                                                {playerAverage.toFixed(2)}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {selectedGroupB.length > 0 && (
                                <div>
                                  <h5 className="text-[10px] font-mono uppercase tracking-[0.14em] text-sky-400 mb-2">Group B</h5>
                                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-1.5">
                                    {selectedGroupB.map((player) => {
                                      const enabledKeys = getEnabledKeys();
                                      const playerData = voteData.leaderboard.find(p => p.name === player.name);
                                      const playerImageSrc = playerData ? aflPlayerData.find(p => p.player_name === playerData.name)?.image_src : null;
                                      
                                      // Calculate player's average from enabled datasets
                                      const playerAverage = playerData ? (() => {
                                        const validKeys = enabledKeys.filter(k => playerData[k] !== undefined);
                                        if (validKeys.length === 0) return 0;
                                        const sum = validKeys.reduce((acc, k) => acc + (Number(playerData[k]) || 0), 0);
                                        return sum / validKeys.length;
                                      })() : 0;

                                      return (
                                        <div key={player.name} className="bg-slate-900/60 rounded-lg border border-slate-700/60 p-2 text-center relative group overflow-hidden hover:border-slate-600 transition-colors">
                                          {/* Remove button */}
                                          <button
                                            onClick={() => removePlayerFromGroup(player.name, 'B')}
                                            className="absolute top-1 right-1 w-4 h-4 bg-rose-500/90 hover:bg-rose-500 rounded-full flex items-center justify-center text-white opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
                                            title={`Remove ${player.name}`}
                                          >
                                            <Trash2 className="w-2.5 h-2.5" />
                                          </button>
                                          <div className="flex flex-col items-center space-y-1">
                                            {playerImageSrc ? (
                                              <img 
                                                src={playerImageSrc} 
                                                alt={player.name}
                                                className="w-8 h-8 rounded-full object-cover ring-1 ring-slate-700/60"
                                              />
                                            ) : (
                                              <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-600 rounded-full flex items-center justify-center ring-1 ring-slate-700/60">
                                                <span className="text-white font-bold text-xs">{player.name.charAt(0)}</span>
                                              </div>
                                            )}
                                            <div className="w-full">
                                              <div className="text-white text-xs font-semibold truncate">
                                                {player.name}
                                              </div>
                                              <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500 truncate">
                                                {playerData?.team || 'Unknown'}
                                              </div>
                                              <div className="text-sky-300 text-xs font-mono font-bold tabular-nums">
                                                {playerAverage.toFixed(2)}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </>
                          ) : selectedBetType === 'players-vs-team' ? (
                            // Display players and team for vs Team bet type
                            <>
                              {selectedPlayers.length > 0 && (
                                <div className="mb-3">
                                  <h5 className="text-[10px] font-mono uppercase tracking-[0.14em] text-emerald-400 mb-2">Players</h5>
                                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-1.5">
                                    {selectedPlayers.map((player) => {
                                      const enabledKeys = getEnabledKeys();
                                      const playerData = voteData.leaderboard.find(p => p.name === player.name);
                                      const playerImageSrc = playerData ? aflPlayerData.find(p => p.player_name === playerData.name)?.image_src : null;
                                      
                                      // Calculate player's average from enabled datasets
                                      const playerAverage = playerData ? (() => {
                                        const validKeys = enabledKeys.filter(k => playerData[k] !== undefined);
                                        if (validKeys.length === 0) return 0;
                                        const sum = validKeys.reduce((acc, k) => acc + (Number(playerData[k]) || 0), 0);
                                        return sum / validKeys.length;
                                      })() : 0;

                                      return (
                                        <div key={player.name} className="bg-slate-900/60 rounded-lg border border-slate-700/60 p-2 text-center relative group overflow-hidden hover:border-slate-600 transition-colors">
                                          {/* Remove button */}
                                          <button
                                            onClick={() => removePlayer(player.name)}
                                            className="absolute top-1 right-1 w-4 h-4 bg-rose-500/90 hover:bg-rose-500 rounded-full flex items-center justify-center text-white opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
                                            title={`Remove ${player.name}`}
                                          >
                                            <Trash2 className="w-2.5 h-2.5" />
                                          </button>
                                          <div className="flex flex-col items-center space-y-1">
                                            {playerImageSrc ? (
                                              <img 
                                                src={playerImageSrc} 
                                                alt={player.name}
                                                className="w-8 h-8 rounded-full object-cover ring-1 ring-slate-700/60"
                                              />
                                            ) : (
                                              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center ring-1 ring-slate-700/60">
                                                <span className="text-white font-bold text-xs">{player.name.charAt(0)}</span>
                                              </div>
                                            )}
                                            <div className="w-full">
                                              <div className="text-white text-xs font-semibold truncate">
                                                {player.name}
                                              </div>
                                              <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500 truncate">
                                                {playerData?.team || 'Unknown'}
                                              </div>
                                              <div className="text-emerald-300 text-xs font-mono font-bold tabular-nums">
                                                {playerAverage.toFixed(2)}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {selectedTeam && (
                                <div>
                                  <h5 className="text-[10px] font-mono uppercase tracking-[0.14em] text-sky-400 mb-2">Selected Team</h5>
                                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-1.5">
                                    <div className="bg-slate-900/60 rounded-lg border border-slate-700/60 p-2 text-center relative group overflow-hidden hover:border-slate-600 transition-colors">
                                      {/* Remove button */}
                                      <button
                                        onClick={() => setSelectedTeam(null)}
                                        className="absolute top-1 right-1 w-4 h-4 bg-rose-500/90 hover:bg-rose-500 rounded-full flex items-center justify-center text-white opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
                                        title={`Remove ${getTeamDisplayName(selectedTeam)}`}
                                      >
                                        <Trash2 className="w-2.5 h-2.5" />
                                      </button>
                                      <div className="flex flex-col items-center space-y-1">
                                        {getTeamLogo(selectedTeam) ? (
                                          <img 
                                            src={getTeamLogo(selectedTeam)} 
                                            alt={getTeamDisplayName(selectedTeam)}
                                            className="w-8 h-8 object-contain"
                                          />
                                        ) : (
                                          <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-600 rounded-full flex items-center justify-center ring-1 ring-slate-700/60">
                                            <span className="text-white font-bold text-xs">{getTeamDisplayName(selectedTeam).charAt(0)}</span>
                                          </div>
                                        )}
                                        <div className="w-full">
                                          <div className="text-white text-xs font-semibold truncate">
                                            {getTeamDisplayName(selectedTeam)}
                                          </div>
                                          <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500 truncate">
                                            Team Total
                                          </div>
                                          <div className="text-sky-300 text-xs font-mono font-bold tabular-nums">
                                            {(() => {
                                              // Calculate team's average total votes across all enabled datasets
                                              if (!selectedTeam || !voteData?.leaderboard) return '0.00';
                                              
                                              const enabledKeys = getEnabledKeys();
                                              let totalVotes = 0;
                                              let validCounts = 0;
                                              
                                              enabledKeys.forEach(key => {
                                                let keyTotal = 0;
                                                voteData.leaderboard.forEach(player => {
                                                  const playerTeam = player.full_team_name || player.team;
                                                  if (playerTeam === selectedTeam && player[key] !== undefined) {
                                                    keyTotal += player[key];
                                                  }
                                                });
                                                totalVotes += keyTotal;
                                                validCounts++;
                                              });
                                              
                                              const average = validCounts > 0 ? totalVotes / validCounts : 0;
                                              return average.toFixed(2);
                                            })()}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            // Display single group for other bet types (players-combine-x and players-all-poll-x)
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-1.5">
                              {selectedPlayers.map((player) => {
                            const enabledKeys = getEnabledKeys();
                            const playerData = voteData.leaderboard.find(p => p.name === player.name);
                            const playerImageSrc = playerData ? aflPlayerData.find(p => p.player_name === playerData.name)?.image_src : null;
                            
                            // Calculate player's average from enabled datasets
                            const playerAverage = playerData ? (() => {
                              const validKeys = enabledKeys.filter(k => playerData[k] !== undefined);
                              if (validKeys.length === 0) return 0;
                              const sum = validKeys.reduce((acc, k) => acc + (Number(playerData[k]) || 0), 0);
                              return sum / validKeys.length;
                            })() : 0;

                            return (
                              <div key={player.name} className="bg-slate-900/60 rounded-lg border border-slate-700/60 p-2 text-center relative group overflow-hidden hover:border-slate-600 transition-colors">
                                {/* Remove button - visible on mobile, hover on desktop */}
                                <button
                                  onClick={() => removePlayer(player.name)}
                                  className="absolute top-1 right-1 w-4 h-4 bg-rose-500/90 hover:bg-rose-500 rounded-full flex items-center justify-center text-white opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
                                  title={`Remove ${player.name}`}
                                >
                                  <Trash2 className="w-2.5 h-2.5" />
                                </button>
                                <div className="flex flex-col items-center space-y-1">
                                  {playerImageSrc ? (
                                    <img 
                                      src={playerImageSrc} 
                                      alt={player.name}
                                      className="w-8 h-8 rounded-full object-cover ring-1 ring-slate-700/60"
                                    />
                                  ) : (
                                    <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center ring-1 ring-slate-700/60">
                                      <span className="text-white font-bold text-xs">{player.name.charAt(0)}</span>
                                    </div>
                                  )}
                                  <div className="w-full">
                                    <div className="text-white text-xs font-semibold truncate">
                                      {player.name}
                                    </div>
                                    <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500 truncate">
                                      {playerData?.team || 'Unknown'}
                                    </div>
                                    <div className="text-emerald-300 text-xs font-mono font-bold tabular-nums">
                                      {playerAverage.toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Right Side - Chart and Table */}
                  <div className="w-full lg:flex-1 flex flex-col gap-3 min-h-0 min-w-0 lg:overflow-hidden">
                    {/* Vote Chart */}
                    {selectedBetType !== 'players-all-poll-x' && (
                      <div className="flex-1 min-h-[260px] sm:min-h-[300px] lg:min-h-0">
                        {selectedBetType === 'players-a-vs-b' ? (
                          <BYOComparisonChart 
                            groupAPlayers={selectedGroupA}
                            groupBPlayers={selectedGroupB}
                            voteData={voteData}
                            getEnabledKeys={getEnabledKeys}
                          />
                        ) : selectedBetType === 'players-vs-team' ? (
                          <BYOComparisonChart 
                            groupAPlayers={selectedPlayers}
                            groupBPlayers={[]} // Team will be handled differently in the chart
                            selectedTeam={selectedTeam}
                            voteData={voteData}
                            getEnabledKeys={getEnabledKeys}
                            isTeamComparison={true}
                          />
                        ) : (
                          <BYOVoteBarChart 
                            counts={chartCounts}
                            labels={chartLabels}
                            threshold={voteThreshold}
                          />
                        )}
                      </div>
                    )}


                    {/* Threshold Difference Table - Only show when players are selected (exclude players-all-poll-x) */}
                    {((selectedBetType === 'players-combine-x' && selectedPlayers.length > 0 && chartCounts.length > 0) ||
                      (selectedBetType === 'players-a-vs-b' && (selectedGroupA.length > 0 || selectedGroupB.length > 0)) ||
                      (selectedBetType === 'players-vs-team' && (selectedPlayers.length > 0 || selectedTeam))) && (
                      <div className={`bg-slate-950/60 rounded-xl border border-slate-800 overflow-hidden flex flex-col flex-shrink-0 min-w-0 ${
                        selectedBetType === 'players-all-poll-x' ? 'lg:flex-1' : 'h-[120px] sm:h-[140px] lg:h-[120px]'
                      }`}>
                        <div className="flex-1 overflow-auto min-w-0 styled-scrollbar">
                          <div className="overflow-x-auto w-full">
                            {selectedBetType === 'players-a-vs-b' ? (
                              // A vs B comparison table - horizontal layout showing Group A and differences
                              <table className="w-full h-full min-w-max">
                                <thead className="sticky top-0 bg-[#0e1625] z-10">
                                  <tr className="border-b border-slate-800/80">
                                    {(() => {
                                      const enabledKeys = getEnabledKeys();
                                      const displayOrder = ['count1','count2','count3','count4','count5','betfair','espn','byb'];
                                      const enabledDisplayKeys = displayOrder.filter((k) => new Set(enabledKeys).has(k));
                                      const keyToLabel = { 
                                        count1: 'BG 1', count2: 'BG 2', count3: 'BG 3', count4: 'BG 4', count5: 'BG 5', 
                                        betfair: 'Betfair', espn: 'ESPN', byb: 'BYB' 
                                      };
                                      return enabledDisplayKeys.map((key, index) => (
                                        <th key={index} className="text-center px-1 sm:px-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">
                                          {keyToLabel[key] === 'Betfair' ? (
                                            <>
                                              <span className="hidden sm:inline">Betfair</span>
                                              <span className="inline sm:hidden">BF</span>
                                            </>
                                          ) : keyToLabel[key]}
                                        </th>
                                      ));
                                    })()}
                                    <th className="text-center px-1 sm:px-2 py-2.5 text-[10px] font-mono font-medium text-emerald-300 uppercase tracking-[0.12em] bg-emerald-500/10 whitespace-nowrap">
                                      BG
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr className="border-b border-slate-800/50">
                                    {(() => {
                                      const enabledKeys = getEnabledKeys();
                                      const displayOrder = ['count1','count2','count3','count4','count5','betfair','espn','byb'];
                                      const enabledDisplayKeys = displayOrder.filter((k) => new Set(enabledKeys).has(k));

                                      const calculateGroupTotal = (players, key) => {
                                        if (!voteData?.leaderboard || players.length === 0) return 0;
                                        let total = 0;
                                        players.forEach(selectedPlayer => {
                                          const playerData = voteData.leaderboard.find(p => p.name === selectedPlayer.name);
                                          if (playerData && playerData[key] !== undefined) {
                                            total += playerData[key];
                                          }
                                        });
                                        return total;
                                      };

                                      // Calculate global average for BG column
                                      const calculateGlobalAverage = (key) => {
                                        if (!voteData?.leaderboard) return 0;
                                        const validValues = voteData.leaderboard
                                          .map(player => Number(player[key]))
                                          .filter(val => !isNaN(val) && val > 0);
                                        return validValues.length > 0 ? validValues.reduce((sum, val) => sum + val, 0) / validValues.length : 0;
                                      };

                                      // Calculate global average across all enabled keys
                                      let globalAverage = 0;
                                      let validGlobalCounts = 0;
                                      enabledKeys.forEach(key => {
                                        const avg = calculateGlobalAverage(key);
                                        if (avg > 0) {
                                          globalAverage += avg;
                                          validGlobalCounts++;
                                        }
                                      });
                                      globalAverage = validGlobalCounts > 0 ? globalAverage / validGlobalCounts : 0;

                                      // Calculate Group A and Group B averages
                                      let totalGroupA = 0;
                                      let totalGroupB = 0;
                                      let validCounts = 0;
                                      enabledKeys.forEach(key => {
                                        const groupATotal = calculateGroupTotal(selectedGroupA, key);
                                        const groupBTotal = calculateGroupTotal(selectedGroupB, key);
                                        totalGroupA += groupATotal;
                                        totalGroupB += groupBTotal;
                                        validCounts++;
                                      });
                                      const groupAAverage = validCounts > 0 ? totalGroupA / validCounts : 0;
                                      const groupBAverage = validCounts > 0 ? totalGroupB / validCounts : 0;
                                      const bgDifference = groupAAverage - groupBAverage;
                                      const bgMaxValue = Math.max(groupAAverage, groupBAverage);
                                      const bgIsWinner = groupAAverage >= bgMaxValue && bgMaxValue > 0;

                                      // Create all table cells
                                      const tableCells = [];
                                      
                                      // Add count columns
                                      enabledDisplayKeys.forEach((key, index) => {
                                        const groupATotal = calculateGroupTotal(selectedGroupA, key);
                                        const groupBTotal = calculateGroupTotal(selectedGroupB, key);
                                        const difference = groupATotal - groupBTotal;
                                        const maxValue = Math.max(groupATotal, groupBTotal);
                                        const isWinner = groupATotal >= maxValue && maxValue > 0;
                                        
                                        tableCells.push(
                                          <td key={index} className={`px-1 sm:px-2 py-2 text-center whitespace-nowrap ${isWinner ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : ''}`}>
                                            <div className="flex flex-col items-center">
                                              <span className="text-slate-300 font-mono font-semibold tabular-nums text-xs sm:text-[13px]">
                                                {groupATotal.toFixed(2)}
                                              </span>
                                              <span className={`text-[10px] font-mono tabular-nums mt-0.5 ${difference >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                                {difference >= 0 ? '+' : ''}{difference.toFixed(2)}
                                              </span>
                                              {isWinner && (
                                                <LeaderCheck />
                                              )}
                                            </div>
                                          </td>
                                        );
                                      });
                                      
                                      // Add BG column
                                      tableCells.push(
                                        <td key="bg" className={`px-1 sm:px-2 py-2 text-center whitespace-nowrap ${bgIsWinner ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : ''}`}>
                                          <div className="flex flex-col items-center">
                                            <span className="text-slate-300 font-mono font-semibold tabular-nums text-xs sm:text-[13px]">
                                              {groupAAverage.toFixed(2)}
                                            </span>
                                            <span className={`text-[10px] font-mono tabular-nums mt-0.5 ${bgDifference >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                              {bgDifference >= 0 ? '+' : ''}{bgDifference.toFixed(2)}
                                            </span>
                                            {bgIsWinner && (
                                              <LeaderCheck />
                                            )}
                                          </div>
                                        </td>
                                      );

                                      return tableCells;
                                    })()}
                                  </tr>
                                </tbody>
                              </table>
                            ) : selectedBetType === 'players-vs-team' ? (
                              // Players vs Team comparison table - similar to A vs B but with team data
                              <table className="w-full h-full min-w-max">
                                <thead className="sticky top-0 bg-[#0e1625] z-10">
                                  <tr className="border-b border-slate-800/80">
                                    {(() => {
                                      const enabledKeys = getEnabledKeys();
                                      const displayOrder = ['count1','count2','count3','count4','count5','betfair','espn','byb'];
                                      const enabledDisplayKeys = displayOrder.filter((k) => new Set(enabledKeys).has(k));
                                      const keyToLabel = { 
                                        count1: 'BG 1', count2: 'BG 2', count3: 'BG 3', count4: 'BG 4', count5: 'BG 5', 
                                        betfair: 'Betfair', espn: 'ESPN', byb: 'BYB' 
                                      };
                                      return enabledDisplayKeys.map((key, index) => (
                                        <th key={index} className="text-center px-1 sm:px-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">
                                          {keyToLabel[key] === 'Betfair' ? (
                                            <>
                                              <span className="hidden sm:inline">Betfair</span>
                                              <span className="inline sm:hidden">BF</span>
                                            </>
                                          ) : keyToLabel[key]}
                                        </th>
                                      ));
                                    })()}
                                    <th className="text-center px-1 sm:px-2 py-2.5 text-[10px] font-mono font-medium text-emerald-300 uppercase tracking-[0.12em] bg-emerald-500/10 whitespace-nowrap">
                                      BG
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr className="border-b border-slate-800/50">
                                    {(() => {
                                      const enabledKeys = getEnabledKeys();
                                      const displayOrder = ['count1','count2','count3','count4','count5','betfair','espn','byb'];
                                      const enabledDisplayKeys = displayOrder.filter((k) => new Set(enabledKeys).has(k));

                                      const calculatePlayersTotal = (key) => {
                                        if (!voteData?.leaderboard || selectedPlayers.length === 0) return 0;
                                        let total = 0;
                                        selectedPlayers.forEach(selectedPlayer => {
                                          const playerData = voteData.leaderboard.find(p => p.name === selectedPlayer.name);
                                          if (playerData && playerData[key] !== undefined) {
                                            total += playerData[key];
                                          }
                                        });
                                        return total;
                                      };

                                      const calculateTeamTotal = (key) => {
                                        if (!voteData?.leaderboard || !selectedTeam) return 0;
                                        let total = 0;
                                        voteData.leaderboard.forEach(player => {
                                          const playerTeam = player.full_team_name || player.team;
                                          if (playerTeam === selectedTeam && player[key] !== undefined) {
                                            total += player[key];
                                          }
                                        });
                                        return total;
                                      };

                                      // Calculate averages for BG column
                                      let playersAverage = 0;
                                      let teamAverage = 0;
                                      let validCounts = 0;
                                      enabledKeys.forEach(key => {
                                        const playersTotal = calculatePlayersTotal(key);
                                        const teamTotal = calculateTeamTotal(key);
                                        playersAverage += playersTotal;
                                        teamAverage += teamTotal;
                                        validCounts++;
                                      });
                                      playersAverage = validCounts > 0 ? playersAverage / validCounts : 0;
                                      teamAverage = validCounts > 0 ? teamAverage / validCounts : 0;
                                      const bgDifference = playersAverage - teamAverage;
                                      const bgMaxValue = Math.max(playersAverage, teamAverage);
                                      const bgIsWinner = playersAverage >= bgMaxValue && bgMaxValue > 0;

                                      // Create all table cells
                                      const tableCells = [];
                                      
                                      // Add count columns
                                      enabledDisplayKeys.forEach((key, index) => {
                                        const playersTotal = calculatePlayersTotal(key);
                                        const teamTotal = calculateTeamTotal(key);
                                        const difference = playersTotal - teamTotal;
                                        const maxValue = Math.max(playersTotal, teamTotal);
                                        const isWinner = playersTotal >= maxValue && maxValue > 0;
                                        
                                        tableCells.push(
                                          <td key={index} className={`px-1 sm:px-2 py-2 text-center whitespace-nowrap ${isWinner ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : ''}`}>
                                            <div className="flex flex-col items-center">
                                              <span className="text-slate-300 font-mono font-semibold tabular-nums text-xs sm:text-[13px]">
                                                {playersTotal.toFixed(2)}
                                              </span>
                                              <span className={`text-[10px] font-mono tabular-nums mt-0.5 ${difference >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                                {difference >= 0 ? '+' : ''}{difference.toFixed(2)}
                                              </span>
                                              {isWinner && (
                                                <LeaderCheck />
                                              )}
                                            </div>
                                          </td>
                                        );
                                      });
                                      
                                      // Add BG column
                                      tableCells.push(
                                        <td key="bg" className={`px-1 sm:px-2 py-2 text-center whitespace-nowrap ${bgIsWinner ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : ''}`}>
                                          <div className="flex flex-col items-center">
                                            <span className="text-slate-300 font-mono font-semibold tabular-nums text-xs sm:text-[13px]">
                                              {playersAverage.toFixed(2)}
                                            </span>
                                            <span className={`text-[10px] font-mono tabular-nums mt-0.5 ${bgDifference >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                              {bgDifference >= 0 ? '+' : ''}{bgDifference.toFixed(2)}
                                            </span>
                                            {bgIsWinner && (
                                              <LeaderCheck />
                                            )}
                                          </div>
                                        </td>
                                      );

                                      return tableCells;
                                    })()}
                                  </tr>
                                </tbody>
                              </table>
                            ) : (
                              // Original horizontal table for combine-x bet type
                              <table className="w-full h-full min-w-max">
                                <thead className="sticky top-0 bg-[#0e1625] z-10">
                                  <tr className="border-b border-slate-800/80">
                                    {chartLabels.map((label, index) => (
                                      <th key={index} className="text-center px-1 sm:px-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">
                                        {label === 'Betfair' ? (
                                          <>
                                            <span className="hidden sm:inline">Betfair</span>
                                            <span className="inline sm:hidden">BF</span>
                                          </>
                                        ) : label}
                                      </th>
                                    ))}
                                    <th className="text-center px-1 sm:px-2 py-2.5 text-[10px] font-mono font-medium text-emerald-300 uppercase tracking-[0.12em] bg-emerald-500/10 whitespace-nowrap">
                                      BG
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr className="border-b border-slate-800/50">
                                    {chartCounts.map((count, index) => {
                                      const difference = count - voteThreshold;
                                      const isAboveThreshold = count >= voteThreshold;
                                      
                                      return (
                                        <td key={index} className={`px-1 sm:px-2 py-2 text-center whitespace-nowrap ${isAboveThreshold ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : ''}`}>
                                          <div className="flex flex-col items-center">
                                            <span className="text-slate-300 font-mono font-semibold tabular-nums text-xs sm:text-[13px]">
                                              {count.toFixed(2)}
                                            </span>
                                            <span className={`text-[10px] font-mono tabular-nums mt-0.5 ${difference >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                              {difference >= 0 ? '+' : ''}{difference.toFixed(2)}
                                            </span>
                                            {isAboveThreshold && (
                                              <LeaderCheck />
                                            )}
                                          </div>
                                        </td>
                                      );
                                    })}
                                    {/* BG Column */}
                                    <td className={`px-1 sm:px-2 py-2 text-center whitespace-nowrap ${globalAverage >= voteThreshold ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : ''}`}>
                                      <div className="flex flex-col items-center">
                                        <span className="text-slate-300 font-mono font-semibold tabular-nums text-xs sm:text-[13px]">
                                          {globalAverage.toFixed(2)}
                                        </span>
                                        <span className={`text-[10px] font-mono tabular-nums mt-0.5 ${(globalAverage - voteThreshold) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                          {(globalAverage - voteThreshold) >= 0 ? '+' : ''}{(globalAverage - voteThreshold).toFixed(2)}
                                        </span>
                                        {globalAverage >= voteThreshold && (
                                          <LeaderCheck />
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BYOBrownlow;